/**
 * @fileoverview "+ Add class" — put a class inside a program.
 *
 * The class is owned by the instructor who teaches it, not by the
 * organization: every roster, shared lesson and teaching attribution keys off
 * the class's owner. So the org names a head instructor.
 *
 * Naming one is OPTIONAL, because a term is usually built before it is
 * staffed: the classes are known in June and the teachers are signed in
 * August. An assigned class can have its term built before teachers are named;
 * reserving instructor calendars and writing lessons require an instructor.
 *
 * **Who it CAN name is the ORG's instructors, not the program's.** Sourcing
 * that list from program membership made a fresh program unusable in the other
 * direction: an org with three instructors and no teaching history yet saw an
 * empty picker, and every choice it could have made was refused. Someone the
 * org employs can be given a class here, and the class joins them to the
 * program on the way — the step the admin would otherwise have to go and do
 * first.
 *
 * Someone who has only been INVITED is shown and disabled rather than hidden,
 * so an admin can see the invitation is out instead of wondering where they
 * went. They cannot be given a class: stamping one with their name would
 * attribute teaching to a person who has not agreed to teach here. Naming the
 * class on their invitation is the way to line it up — it is theirs the moment
 * they accept.
 *
 * Only offered on a class or camp. A private-lessons program is scheduled by
 * its bookings and has nothing for a class to be.
 *
 * **Start from a template.** Any current or archived class in the
 * organization's programs can be copied: its name, description and type fill
 * in the form, and the server copies whatever else is ticked (instructors who
 * belong to the org, room and notes, the meeting pattern and holidays as a
 * draft for the schedule, curriculum, files, and the roster when the class is
 * filed under the same instructor). Asked for the template's instructors with
 * nobody chosen here, the server files the class under the template's own.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Icon, Tag } from "@/components/studio/primitives";
import TemplatePickerDialog, { serverParts, skippedLine } from "@/components/org/TemplatePickerDialog";

const CLASS_KIND_OPTIONS = [
  { id: "studio_class", label: "Class", desc: "A regular group that meets on a schedule." },
  { id: "ensemble", label: "Ensemble", desc: "A performing group." },
  { id: "cohort", label: "Cohort", desc: "A group moving through a course together." },
];

/* What a new class can take from an existing one; `client` parts fill in the
   form, the rest are copied once the class exists (services/program_templates). */
export const CLASS_TEMPLATE_PARTS = [
  { id: "details", label: "Name, description and type", desc: "Filled in below, ready to change.", client: true },
  { id: "instructors", label: "Instructors", desc: "The same instructors, if they belong to your organization." },
  { id: "room_notes", label: "Room and notes", desc: "Where to be and what to know." },
  { id: "timetable", label: "Meeting days and times", desc: "Fills in the schedule. You preview it before anything is scheduled." },
  { id: "holidays", label: "Holidays and no-class days" },
  { id: "curriculum", label: "Curriculum" },
  { id: "files", label: "Files" },
  { id: "roster", label: "Students", desc: "Only when the class is filed under the template's own instructor." },
];

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

export default function AddClassDialog({
  open,
  onOpenChange,
  programId,
  /** Instructors already on this program — always selectable, no lookup needed. */
  instructors = [],
  /** Invitations out on this program, shown as provisional and not selectable. */
  invited = [],
  onCreated,
}) {
  const [form, setForm] = useState({ name: "", description: "", kind: "studio_class", instructor_id: "", scheduling_mode: "assigned" });
  const [saving, setSaving] = useState(false);
  const [orgInstructors, setOrgInstructors] = useState([]);
  // Start from a template: the picker, the programs it can draw from, the
  // classes of the one chosen, and what was picked.
  const [picking, setPicking] = useState(false);
  const [templatePrograms, setTemplatePrograms] = useState([]);
  const [templateProgram, setTemplateProgram] = useState(programId);
  const [templateClasses, setTemplateClasses] = useState(null);
  const [template, setTemplate] = useState(null); // {class_id, name, parts, chosen, program_id}

  const memberIds = useMemo(() => new Set(instructors.map((i) => i.user_id)), [instructors]);

  /* The org's own instructors, so a program with nobody on it yet still has
     people to choose from. Failing quietly is right here: the program's own
     members are already in hand, and losing the wider list must not take the
     dialog down with it. */
  const loadOrgInstructors = useCallback(async () => {
    if (!open) return;
    try {
      const res = await orgApi.get("/org/instructors");
      setOrgInstructors(res.data?.instructors || []);
    } catch {
      setOrgInstructors([]);
    }
  }, [open]);

  useEffect(() => { loadOrgInstructors(); }, [loadOrgInstructors]);

  /** Everyone selectable, program members first — they are the likely answer. */
  const choices = useMemo(() => {
    const rows = instructors.map((i) => ({ user_id: i.user_id, name: i.name, onProgram: true }));
    for (const i of orgInstructors) {
      if (memberIds.has(i.user_id)) continue;
      rows.push({ user_id: i.user_id, name: i.name || i.email, onProgram: false });
    }
    return rows;
  }, [instructors, orgInstructors, memberIds]);

  /* Cleared when the dialog OPENS, and never again. The org list arrives a
     moment after the dialog does, and a reset keyed on the candidates would
     wipe whatever the admin had already typed the instant it landed. */
  useEffect(() => {
    if (!open) return;
    setForm({ name: "", description: "", kind: "studio_class", instructor_id: "", scheduling_mode: "assigned" });
    setTemplate(null);
    setTemplateProgram(programId);
  }, [open, programId]);

  /* The org's class programs, read when the picker first opens. */
  useEffect(() => {
    if (!picking || templatePrograms.length) return;
    orgApi
      .get("/org/programs")
      .then((res) =>
        setTemplatePrograms(
          (res.data?.programs || [])
            .filter((p) => ["class", "camp"].includes(p.program_type))
            .map((p) => ({ id: p.program_id, name: p.lifecycle === "past" ? `${p.title} (past)` : p.title })),
        ),
      )
      .catch(() => setTemplatePrograms([]));
  }, [picking, templatePrograms.length]);

  /* The classes of the program the picker is showing. */
  useEffect(() => {
    if (!picking || !templateProgram) return;
    let live = true;
    setTemplateClasses(null);
    orgApi
      .get(`/org/programs/${templateProgram}/classes`)
      .then((res) => live && setTemplateClasses(res.data?.classes || []))
      .catch(() => live && setTemplateClasses([]));
    return () => {
      live = false;
    };
  }, [picking, templateProgram]);

  const templateSources = useMemo(
    () =>
      (templateClasses || [])
        .filter((c) => !c.connected)
        .map((c) => ({ id: c.class_id, name: c.name, hint: c.archived ? "Archived" : c.instructor_name || "Unassigned" })),
    [templateClasses],
  );

  const applyTemplate = ({ source, parts }) => {
    const c = (templateClasses || []).find((x) => x.class_id === source.id);
    if (!c) return;
    const has = (id) => parts.includes(id);
    setTemplate({ class_id: c.class_id, name: c.name, parts: serverParts(CLASS_TEMPLATE_PARTS, parts), chosen: parts, program_id: templateProgram });
    setForm((f) => ({
      ...f,
      ...(has("details") ? { name: c.name || "", description: c.description || "", kind: c.kind || "studio_class" } : {}),
      // Left for the server to choose from the template's own instructors,
      // so the roster can come with them; a choice made below still wins.
      ...(has("instructors") ? { instructor_id: "" } : {}),
      scheduling_mode: "assigned",
    }));
  };

  /* One choice is not a choice: preselect it whenever the list settles on a
     single candidate and nothing has been picked. Guarded on "nothing picked"
     so this can never overwrite a deliberate choice. */
  useEffect(() => {
    if (!open || choices.length !== 1) return;
    setForm((f) => (f.instructor_id ? f : { ...f, instructor_id: choices[0].user_id }));
  }, [open, choices]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const chosen = choices.find((c) => c.user_id === form.instructor_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give the class a name");
      return;
    }
    setSaving(true);
    try {
      const res = await orgApi.post(`/org/programs/${programId}/classes`, {
        name: form.name.trim(),
        description: form.description.trim(),
        kind: form.kind,
        scheduling_mode: form.scheduling_mode,
        instructor_id: form.instructor_id || undefined,
        ...(template?.parts.length ? { template: { class_id: template.class_id, parts: template.parts } } : {}),
      });
      const summary = res.data?.template;
      if (summary) {
        toast.success(`${form.name.trim()} added from ${template.name}. Check its schedule next.`, {
          description: skippedLine(summary),
        });
      } else {
        toast.success(
          !form.instructor_id
            ? `${form.name.trim()} added — assign an instructor to schedule it`
            : res.data?.instructor_joined_program
              ? `${form.name.trim()} added — ${chosen?.name || "they"} joined the program`
              : `${form.name.trim()} added — now give it a schedule`,
        );
      }
      onCreated?.(res.data?.class);
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't add that class"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Add a class</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="add-class-form">
          {template ? (
            <div
              className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] px-4 py-3"
              data-testid="class-template-chosen"
            >
              <span className="flex min-w-0 items-center gap-2 font-sans text-[14px] text-[var(--text-secondary)]">
                <Icon name="copy" size={15} className="shrink-0 text-[var(--text-tertiary)]" />
                <span className="min-w-0">
                  From <span className="font-medium text-[var(--text-primary)]">{template.name}</span>
                  {template.parts.length > 0 && ` · ${template.parts.length} more copied when you add it`}
                </span>
              </span>
              <span className="flex items-center gap-3">
                <button type="button" onClick={() => setPicking(true)} data-testid="class-template-change" className="font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] hover:underline">
                  Change
                </button>
                <button type="button" onClick={() => setTemplate(null)} data-testid="class-template-clear" className="font-sans text-[13px] text-[var(--text-tertiary)] hover:underline">
                  Start blank
                </button>
              </span>
            </div>
          ) : (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <Button variant="ghost" size="sm" icon="copy" onPress={() => setPicking(true)} data-testid="class-template-open">
                Start from a template
              </Button>
              <span className="font-sans text-[13px] text-[var(--text-tertiary)]">Copy an existing class.</span>
            </div>
          )}
          <label className="mb-4 block text-sm text-[var(--text-secondary)]">How students join
            <select className={inputClass} value={form.scheduling_mode} onChange={set("scheduling_mode")}>
              <option value="assigned">The organization assigns the class</option>
              <option value="booked">Families book places in Scheduler</option>
            </select>
          </label>
          <input
            value={form.name}
            onChange={set("name")}
            placeholder="Class name — e.g. Tuesday beginners"
            aria-label="Class name"
            data-testid="class-name"
            className={inputClass}
          />
          <textarea
            value={form.description}
            onChange={set("description")}
            placeholder="A line about this class"
            rows={2}
            maxLength={280}
            aria-label="Class details"
            data-testid="class-description"
            className={`${inputClass} mt-3`}
          />

          <div className="mt-4">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Type</div>
            <select value={form.kind} onChange={set("kind")} aria-label="Class type" data-testid="class-kind" className={inputClass}>
              {CLASS_KIND_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
              {CLASS_KIND_OPTIONS.find((o) => o.id === form.kind)?.desc}
            </p>
          </div>

          <div className="mt-4">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Head instructor</div>
            {choices.length === 0 ? (
              <p data-testid="class-no-instructors" className="font-sans text-[14px] text-[var(--text-tertiary)]">
                Nobody has joined your organization yet — add the class now and assign someone once they accept.
                You can name it on their invitation, and it&rsquo;s theirs the moment they say yes.
              </p>
            ) : (
              <>
                <select
                  value={form.instructor_id}
                  onChange={set("instructor_id")}
                  aria-label="Head instructor"
                  data-testid="class-instructor"
                  className={inputClass}
                >
                  <option value="">Assign later</option>
                  {choices.map((i) => (
                    <option key={i.user_id} value={i.user_id}>
                      {i.onProgram ? i.name : `${i.name} — not on this program yet`}
                    </option>
                  ))}
                </select>
                {chosen && !chosen.onProgram && (
                  <p data-testid="class-will-join" className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                    {chosen.name} will be added to this program.
                  </p>
                )}
              </>
            )}

            {/* Shown, not hidden: an admin who just sent an invitation should
                see it standing there rather than an empty space. Naming the
                class on that invitation is what lines it up for them. */}
            {invited.length > 0 && (
              <div className="mt-3" data-testid="class-invited">
                <div className="mb-1 font-sans text-[12px] text-[var(--text-tertiary)]">Waiting on them</div>
                <div className="flex flex-col gap-1">
                  {invited.map((i) => (
                    <div key={i.invite_id} className="flex items-center justify-between gap-3 opacity-60">
                      <span className="min-w-0 truncate font-sans text-[14px] text-[var(--text-secondary)]">
                        {i.name || i.email}
                      </span>
                      <Tag>Invited</Tag>
                    </div>
                  ))}
                </div>
                <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                  They can take a class once they accept — or name one on their invitation.
                </p>
              </div>
            )}
            {!form.instructor_id && template?.parts.includes("instructors") ? (
              <p data-testid="class-template-instructors" className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                Left empty, the class goes to {template.name}&rsquo;s instructors who belong to your organization.
              </p>
            ) : !form.instructor_id && (
              <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                {form.scheduling_mode === "assigned" ? "You can build the term now. Assign an instructor before publishing to their calendar or writing a lesson."
                  : "Assign an instructor before setting up bookings for this class."}
              </p>
            )}
          </div>

          <div className="mt-5 flex items-center gap-3">
            <Button type="submit" disabled={saving} data-testid="class-submit">
              {saving ? "Adding…" : "Add class"}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
      <TemplatePickerDialog
        open={picking}
        onOpenChange={setPicking}
        title="Start from a template"
        description="Choose a class and what to bring into the new one. Nothing is saved until you add it."
        groupLabel="Program"
        groups={templatePrograms.length ? templatePrograms : [{ id: programId, name: "This program" }]}
        groupValue={templateProgram}
        onGroupChange={setTemplateProgram}
        sourceLabel="Class"
        sources={templateSources}
        loading={templateClasses === null}
        parts={CLASS_TEMPLATE_PARTS}
        initialSource={template?.class_id}
        initialParts={template?.chosen}
        onApply={applyTemplate}
        testId="class-template"
      />
    </Dialog>
  );
}
